import React, { useState, useEffect, useRef, useCallback } from "react";
import _ from 'i18n';
import { connect } from "react-redux";
import EventLineChartLegend from "../EventLineChartLegend";
import {
  fetchRegistrantLineChartCAData,
  fetchRegistrantLineChartData,
} from "../../../../../actions/thunks/eventStats";
import EventLineChartErrorHandler from "../EventLineChartErrorHandler";
import { isLeapYear, showErrorNotification } from "../../../../../services/utils";
import CompareStatsChart from "./CompareStatsChart";
import CompareStatsFilters from "./CompareStatsFilters";
import axios from "axios";
import EventLineChartFormat from "../EventLineChartFormat";
import EventLineChartLegendFormat from "../EventLineChartLegendFormat";
import { getAccumulateEventStats, getActivePremium, getCommandInfo, startCommandCumulateByFormat, startCommandGetCANbrInscriptionByFormat } from "../../../../../actions/thunks/statistics";
import CompareStatsChartFormat from "./CompareStatsChartFormat";


const formatDate = (date) => {
  return `${date.toLocaleString('fr-FR', { day: '2-digit' })}/${date.toLocaleString('fr-FR', { month: '2-digit' })}`;
};


const CompareStats = ({
  eventStatsFilter,
  language,
  chartData1,
  chartDataCA1,
  type,
  legendType,
  updateChartType,
  updateLegendType,
  isFetching: sourceIsFetching,
  isError: sourceIsError,
  updateChartLinesToShowFormat,
  chartLinesToShowFormat,
  chartLinesToShowSeason,
  formatData1,
  formatCAData1,
  isFormatFetching,
  isFormatError,
  seasonsToShow,
  frequency,
  period,
  showAccumulateEventStats,
  accumulateEventStats,
  accumulateEventStatsCA,
  accumulateEventStatsFetching,
  accumulateEventStatsCAFetching,
  formatAccumulateData,
  formatAccumulateCAData,
  showCompareStats,
  activePremium,
  fetchRegistrantLineChartData,
  fetchRegistrantLineChartCAData,
  startCommandGetCANbrInscriptionByFormat,
  getCommandInfo,
  getActivePremium,
  getAccumulateEventStats,
  startCommandCumulateByFormat,
}) => {

  // ─── State ──────────────────────────────────────────────────────────────────

  const [chartLinesToShow, setChartLinesToShow] = useState(
    ['formations', 'cycles', 'saisons', 'monthly_premium', 'yearly_premium', 'congrès', 'cycles_essential']
  );
  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [CAdata, setCAdata] = useState([]);
  const [isError, setIsError] = useState(false);
  const [CAfetching, setCAfetching] = useState(true);
  const [CAisError, setCAisError] = useState(false);
  const [startMonth, setStartMonth] = useState('01');
  const [endMonth, setEndMonth] = useState('12');
  const [year, setYear] = useState(eventStatsFilter.year[0] - 1);
  const [dataFormat, setDataFormat] = useState([]);
  const [CAdataFormat, setCAdataFormat] = useState([]);
  const [showSpinnerFormat, setShowSpinnerFormat] = useState(false);
  const [activePremiumCurrentYear, setActivePremiumCurrentYear] = useState({});
  const [activePremiumPreviousYear, setActivePremiumPreviousYear] = useState({});
  const [accumulateEventStatsCompare, setAccumulateEventStatsCompare] = useState([]);
  const [accumulateEventStatsCACompare, setAccumulateEventStatsCACompare] = useState([]);
  const [accumulateEventStatsCompareFetching, setAccumulateEventStatsCompareFetching] = useState(false);
  const [accumulateEventStatsCACompareFetching, setAccumulateEventStatsCACompareFetching] = useState(false);
  const [formatAccumulateDataCompare, setFormatAccumulateDataCompare] = useState([]);
  const [formatAccumulateCADataCompare, setFormatAccumulateCADataCompare] = useState([]);

  // Refs replacing instance variables
  const commandTimerRef = useRef(null);
  const currRequestIdRef = useRef(null);
  const isFirstRender = useRef(true);
  const prevYear = useRef(year);
  const prevPeriod = useRef(period);

  // ─── Handlers ────────────────────────────────────────────────────────────────

  const showChartLine = useCallback((line) => {
    setChartLinesToShow(prev => [...prev, line]);
  }, []);

  const hideChartLine = useCallback((line) => {
    setChartLinesToShow(prev => prev.filter(chartLine => chartLine != line));
  }, []);

  const updateChartLinesToShow = useCallback((line) => {
    setChartLinesToShow(prev => {
      const found = prev.find(element => element == line);
      if (found) {
        return prev.filter(chartLine => chartLine != line);
      }
      return [...prev, line];
    });
  }, []);

  const handleStartMonthChange = useCallback((newStartMonth) => {
    setEndMonth(prev => {
      if (newStartMonth > prev) return newStartMonth;
      return prev;
    });
    setStartMonth(newStartMonth);
  }, []);

  const handleEndMonthChange = useCallback((newEndMonth) => {
    setStartMonth(prev => {
      if (newEndMonth < prev) return newEndMonth;
      return prev;
    });
    setEndMonth(newEndMonth);
  }, []);

  const handleYearChange = useCallback((newYear) => {
    setYear(newYear);
  }, []);

  const getDateInterval = useCallback(() => {
    const baseYear = eventStatsFilter.year[0];

    const startDate = new Date(year, startMonth - 1, 1);
    const endDate = new Date(year, endMonth, 0);
    const baseEndDate = new Date(baseYear, endMonth, 0);

    const startMonthName = formatDate(startDate);
    let endMonthName = formatDate(endDate);

    if (isLeapYear(baseYear)) {
      endMonthName = formatDate(baseEndDate);
    }

    return { startMonthName, endMonthName };
  }, [year, startMonth, endMonth, eventStatsFilter.year]);

  // ─── Data Fetching ────────────────────────────────────────────────────────────

  const getValuesFromLocalStorage = useCallback(() => {
    const storedStartMonth = localStorage.getItem('startMonth');
    if (storedStartMonth) setStartMonth(storedStartMonth);
    const storedEndMonth = localStorage.getItem('endMonth');
    if (storedEndMonth) setEndMonth(storedEndMonth);
  }, []);

  const fetchData = useCallback(() => {
    const date = period[0];
    const [, monthDate] = date.split("-");
    const previousYearDate = `${year}-${monthDate}`;

    setFetching(true);
    setCAfetching(true);
    setIsError(false);

    fetchRegistrantLineChartData(frequency, frequency === 'DAILY' ? [previousYearDate] : [year], language ? [language] : undefined, 1).then((res) => {
      setFetching(false);
      setData(res.data.data);
      fetchRegistrantLineChartCAData(frequency, frequency === 'DAILY' ? [previousYearDate] : [year], language ? [language] : undefined, 1).then((res) => {
        setCAfetching(false);
        setCAdata(res.data.data);
      }).catch((err) => {
        if (!axios.isCancel(err)) {
          setCAfetching(false);
          setCAisError(true);
          setCAdata([]);
          showErrorNotification(_('error'), _('errorOccured'));
        }
      });
    }).catch((err) => {
      if (!axios.isCancel(err)) {
        setFetching(false);
        setIsError(true);
        setData([]);
        showErrorNotification(_('error'), _('errorOccured'));
      }
    });

    if (frequency === 'MONTHLY') {
      getActivePremium(period, frequency, startMonth, endMonth).then((res) => {
        setActivePremiumCurrentYear(res.value.data.data);
        getActivePremium([year], frequency, startMonth, endMonth).then((res) => {
          setActivePremiumPreviousYear(res.value.data.data);
        });
      });
    }

    if (frequency === 'DAILY') {
      setAccumulateEventStatsCompareFetching(true);
      setAccumulateEventStatsCACompareFetching(true);
      getAccumulateEventStats(frequency, [previousYearDate], language ? [language] : undefined, 'NBR').then((res) => {
        setAccumulateEventStatsCompare(res.value.data.data);
        setAccumulateEventStatsCompareFetching(false);
      });
      getAccumulateEventStats(frequency, [previousYearDate], language ? [language] : undefined, 'CA').then((res) => {
        setAccumulateEventStatsCACompare(res.value.data.data);
        setAccumulateEventStatsCACompareFetching(false);
      });
    }
  }, [year, period, frequency, startMonth, endMonth, fetchRegistrantLineChartData, fetchRegistrantLineChartCAData, getActivePremium, getAccumulateEventStats]);

  const fetchCAData = useCallback(() => {
    const date = period[0];
    const [, monthDate] = date.split("-");
    const previousYearDate = `${year}-${monthDate}`;

    setCAfetching(true);
    setCAisError(false);

    fetchRegistrantLineChartCAData(frequency, frequency === 'DAILY' ? [previousYearDate] : [year], language ? [language] : undefined, true).then((res) => {
      setCAfetching(false);
      setCAdata(res.data.data);
    }).catch((err) => {
      if (!axios.isCancel(err)) {
        setCAfetching(false);
        setCAisError(true);
        setCAdata([]);
        showErrorNotification(_('error'), _('errorOccured'));
      }
    });
  }, [year, period, frequency, fetchRegistrantLineChartCAData]);

  const getCANbrInscriptionByFormat = useCallback(() => {
    setShowSpinnerFormat(true);
    const date = period[0];
    const [, monthDate] = date.split("-");
    const previousYearDate = `${year}-${monthDate}`;

    startCommandGetCANbrInscriptionByFormat(frequency, frequency === 'DAILY' ? [previousYearDate] : [year], language ? [language] : undefined).then((res) => {
      clearInterval(commandTimerRef.current);

      const startCommandTimer = () => {
        currRequestIdRef.current = `COMMAND_REQUEST_${performance.now()}`;
        const prevRequestId = currRequestIdRef.current;

        getCommandInfo(res.value.data.data.command.id).then((resultat) => {
          if (prevRequestId !== currRequestIdRef.current) return;

          if (resultat.data.data.status !== "FINISHED") {
            setShowSpinnerFormat(true);
            return;
          }

          setDataFormat(resultat.data.data.result.nbrInscrits);
          setCAdataFormat(resultat.data.data.result.caffaire);
          clearInterval(commandTimerRef.current);

          if (frequency === 'DAILY' && showAccumulateEventStats) {
            startCommandCumulateByFormat(frequency, frequency === 'DAILY' ? [previousYearDate] : [year], language ? [language] : undefined, 'NBR').then((res) => {
              clearInterval(commandTimerRef.current);

              const startInnerTimer = () => {
                currRequestIdRef.current = `COMMAND_REQUEST_${performance.now()}`;
                const prevInnerRequestId = currRequestIdRef.current;

                getCommandInfo(res.value.data.data.command.id).then((resultat) => {
                  if (prevInnerRequestId !== currRequestIdRef.current) return;
                  if (resultat.data.data.status !== "FINISHED") {
                    setShowSpinnerFormat(true);
                    return;
                  }

                  setFormatAccumulateDataCompare(resultat.data.data.result);
                  clearInterval(commandTimerRef.current);

                  startCommandCumulateByFormat(frequency, frequency === 'DAILY' ? [previousYearDate] : [year], language ? [language] : undefined, 'CA').then((res) => {
                    clearInterval(commandTimerRef.current);

                    const startCATimer = () => {
                      currRequestIdRef.current = `COMMAND_REQUEST_${performance.now()}`;
                      const prevCARequestId = currRequestIdRef.current;

                      getCommandInfo(res.value.data.data.command.id).then((resultat) => {
                        if (prevCARequestId !== currRequestIdRef.current) return;
                        if (resultat.data.data.status !== "FINISHED") {
                          setShowSpinnerFormat(true);
                          return;
                        }

                        setFormatAccumulateCADataCompare(resultat.data.data.result);
                        setShowSpinnerFormat(false);
                        clearInterval(commandTimerRef.current);
                      }).catch(() => {
                        clearInterval(commandTimerRef.current);
                      });
                    };

                    commandTimerRef.current = setInterval(startCATimer, 3 * 1000);
                  });
                }).catch(() => {
                  clearInterval(commandTimerRef.current);
                });
              };

              commandTimerRef.current = setInterval(startInnerTimer, 3 * 1000);
            });
          }

          setShowSpinnerFormat(false);
        }).catch(() => {
          clearInterval(commandTimerRef.current);
        });
      };

      commandTimerRef.current = setInterval(startCommandTimer, 3 * 1000);
    });
  }, [year, period, frequency, showAccumulateEventStats, startCommandGetCANbrInscriptionByFormat, startCommandCumulateByFormat, getCommandInfo]);

  // ─── Lifecycle Effects ────────────────────────────────────────────────────────

  // componentDidMount
  useEffect(() => {
    getValuesFromLocalStorage();
    if (type === 'CA') {
      fetchCAData();
    } else {
      fetchData();
    }
    if (legendType === 'FORMAT') {
      getCANbrInscriptionByFormat();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => clearInterval(commandTimerRef.current);
  }, []);

  // componentDidUpdate: re-fetch when year or period changes
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (prevYear.current !== year || prevPeriod.current !== period) {
      prevYear.current = year;
      prevPeriod.current = period;

      if (type === 'CA') {
        fetchCAData();
      } else {
        fetchData();
      }
      if (legendType === 'FORMAT') {
        getCANbrInscriptionByFormat();
      }
    }
  }, [year, period]);

  // componentDidUpdate: persist startMonth to localStorage and re-fetch active premium
  useEffect(() => {
    if (isFirstRender.current) return;
    localStorage.setItem('startMonth', startMonth);
    if (frequency === 'MONTHLY') {
      getActivePremium(period, frequency, startMonth, endMonth).then((res) => {
        setActivePremiumCurrentYear(res.value.data.data);
        getActivePremium([year], frequency, startMonth, endMonth).then((res) => {
          setActivePremiumPreviousYear(res.value.data.data);
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startMonth]);

  // componentDidUpdate: persist endMonth to localStorage and re-fetch active premium
  useEffect(() => {
    if (isFirstRender.current) return;
    localStorage.setItem('endMonth', endMonth);
    if (frequency === 'MONTHLY') {
      getActivePremium(period, frequency, startMonth, endMonth).then((res) => {
        setActivePremiumCurrentYear(res.value.data.data);
        getActivePremium([year], frequency, startMonth, endMonth).then((res) => {
          setActivePremiumPreviousYear(res.value.data.data);
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endMonth]);

  // ─── Derived values ───────────────────────────────────────────────────────────

  const chartData = data;
  const chartDataCA = CAdata;
  const formatData = dataFormat;
  const formatCAData = CAdataFormat;
  const isFetching = CAfetching;
  const error = CAisError || isError;
  const dateInterval = getDateInterval();

  // ─── Render ───────────────────────────────────────────────────────────────────

  return (
    <React.Fragment>
      <div className={"flex justify-center mt-5"}>
        <hr style={{ width: "100%" }} />
      </div>
      <div>
        <CompareStatsFilters
          startMonth={startMonth}
          endMonth={endMonth}
          handleStartMonthChange={handleStartMonthChange}
          handleEndMonthChange={handleEndMonthChange}
          year={year}
          handleYearChange={handleYearChange}
          type={type}
          legendType={legendType}
          updateChartType={updateChartType}
          updateLegendType={updateLegendType}
          frequency={frequency}
        />
      </div>
      {legendType === 'TYPE' ? (
        <div className="event-registrant-line-chart-body">
          <div className="event-registrant-line-chart-body-aside" style={{ width: '50%' }}>
            <div className={'chart-registrant-number-label mb-4 mt-4'}>
              {_('REGISTRATIONS_COMPARE_STATS') + ' et ' + _('CA_COMPARE_STATS')}
              {' ' + _('FROM') + ' ' + dateInterval.startMonthName + ' ' + _('TO') + ' ' + dateInterval.endMonthName}
            </div>
            <EventLineChartLegend
              updateChartLinesToShow={updateChartLinesToShow}
              chartLinesToShow={chartLinesToShow}
              chartData={chartData}
              chartDataCA={chartDataCA}
              fetching={isFetching || sourceIsFetching}
              isError={error || sourceIsError}
              sourceData={chartData1}
              sourceDataCA={chartDataCA1}
              startMonth={frequency === 'DAILY' ? null : startMonth}
              endMonth={frequency === 'DAILY' ? null : endMonth}
              year={year}
              type={type}
              frequency={frequency}
              sourceYear={eventStatsFilter.year[0]}
              showCompareStats={showCompareStats}
              activePremium={activePremium}
              activePremiumCurrentYear={activePremiumCurrentYear}
              activePremiumPreviousYear={activePremiumPreviousYear}
            />
          </div>
          <div className="event-registrant-line-chart-body-main" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', width: '50%' }}>
            {
              error ?
                <EventLineChartErrorHandler fetchRegistrantLineChartData={fetchData} /> :
                <CompareStatsChart
                  data1={type === 'CA' ? (showAccumulateEventStats ? accumulateEventStatsCA : chartDataCA1) : (showAccumulateEventStats ? accumulateEventStats : chartData1)}
                  data2={type === 'CA' ? (showAccumulateEventStats ? accumulateEventStatsCACompare : chartDataCA) : (showAccumulateEventStats ? accumulateEventStatsCompare : chartData)}
                  chartLinesToShow={chartLinesToShow}
                  fetching={isFetching || sourceIsFetching || accumulateEventStatsCompareFetching || accumulateEventStatsCACompareFetching || accumulateEventStatsFetching || accumulateEventStatsCAFetching}
                  language={language}
                  startMonth={startMonth}
                  endMonth={endMonth}
                  year1={eventStatsFilter.year[0]}
                  year2={year}
                  type={type}
                  updateType={updateChartType}
                  frequency={frequency}
                />
            }
          </div>
        </div>
      ) : (
        <div className="event-registrant-line-chart-body">
          <div className="event-registrant-line-chart-body-aside" style={{ width: '50%' }}>
            <div className={'chart-registrant-number-label mb-4 mt-4'}>
              {_('REGISTRATIONS_COMPARE_STATS') + ' et ' + _('CA_COMPARE_STATS')}
              {' ' + _('FROM') + ' ' + dateInterval.startMonthName + ' ' + _('TO') + ' ' + dateInterval.endMonthName}
            </div>
            <EventLineChartLegendFormat
              updateChartLinesToShow={updateChartLinesToShowFormat}
              chartLinesToShowFormat={chartLinesToShowFormat}
              formatData={formatData}
              formatCAData={formatCAData}
              fetching={isFormatFetching}
              isError={isFormatError}
              type={legendType}
              showSpinner={showSpinnerFormat}
              seasonsToShow={seasonsToShow}
              frequency={frequency}
              period={period}
              sourceFormatData={formatData1}
              sourceFormatDataCA={formatCAData1}
              startMonth={frequency === 'DAILY' ? null : startMonth}
              endMonth={frequency === 'DAILY' ? null : endMonth}
              year={year}
              sourceYear={eventStatsFilter.year[0]}
            />
          </div>
          <div className="event-registrant-line-chart-body-main" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', width: '50%' }}>
            {
              error ?
                <EventLineChartErrorHandler fetchRegistrantLineChartData={fetchData} /> :
                <CompareStatsChartFormat
                  data1={type === 'CA' ? formatCAData1 : formatData1}
                  data2={type === 'CA' ? formatCAData : formatData}
                  chartLinesToShowSeason={chartLinesToShowSeason}
                  fetching={showSpinnerFormat}
                  language={language}
                  startMonth={startMonth}
                  endMonth={endMonth}
                  year1={eventStatsFilter.year[0]}
                  year2={year}
                  type={type}
                  updateType={updateChartType}
                  frequency={frequency}
                  showAccumulateEventStats={showAccumulateEventStats}
                  formatAccumulateData={formatAccumulateData}
                  formatAccumulateCAData={formatAccumulateCAData}
                  formatAccumulateDataCompare={formatAccumulateDataCompare}
                  formatAccumulateCADataCompare={formatAccumulateCADataCompare}
                />
            }
          </div>
        </div>
      )}
    </React.Fragment>
  );
};

const mapStateToProps = state => ({
  eventStatsFilter: state.filters.eventStatsFilter,
  language: state.params.lng,
});

const mapDispatchToProps = {
  fetchRegistrantLineChartData,
  fetchRegistrantLineChartCAData,
  startCommandGetCANbrInscriptionByFormat,
  getCommandInfo,
  getActivePremium,
  getAccumulateEventStats,
  startCommandCumulateByFormat,
};

export default connect(mapStateToProps, mapDispatchToProps)(CompareStats);



// WEBPACK FOOTER //
// ./app/components/Admin/EventStats/EventRegistrantLinechart/CompareStats/CompareStats.js